import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem('vg_user');
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    });
    const [token, setToken] = useState(() => localStorage.getItem('vg_token'));
    const [loading, setLoading] = useState(false);

    const login = useCallback(async (email, password, totpCode) => {
        setLoading(true);
        try {
            const body = { email, password };
            if (totpCode) body.totp_code = totpCode;
            const res = await api.post('/auth/login', body);
            const { access_token, user: u } = res.data;
            localStorage.setItem('vg_token', access_token);
            localStorage.setItem('vg_user', JSON.stringify(u));
            setToken(access_token);
            setUser(u);
            return u;
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('vg_token');
        localStorage.removeItem('vg_user');
        setToken(null);
        setUser(null);
    }, []);

    const refreshMe = useCallback(async () => {
        try {
            const res = await api.get('/auth/me');
            setUser(res.data);
            localStorage.setItem('vg_user', JSON.stringify(res.data));
        } catch (e) {
            // ignore
        }
    }, []);

    useEffect(() => {
        if (token && !user) {
            refreshMe();
        }
    }, [token, user, refreshMe]);

    const hasRole = (...roles) => {
        if (!user) return false;
        if ((user.roles || []).includes('admin')) return true;
        return (user.roles || []).some((r) => roles.includes(r));
    };

    return (
        <AuthContext.Provider value={{ user, token, loading, login, logout, refreshMe, hasRole }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be inside AuthProvider');
    return ctx;
};
